import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle, XCircle, AlertTriangle, Info, X } from 'lucide-react';

const TOAST_TYPES = {
  success: { bg: 'from-green-500 to-emerald-600', icon: CheckCircle, text: 'text-white' },
  error: { bg: 'from-red-500 to-rose-600', icon: XCircle, text: 'text-white' },
  warning: { bg: 'from-orange-500 to-amber-600', icon: AlertTriangle, text: 'text-white' },
  info: { bg: 'from-blue-500 to-cyan-600', icon: Info, text: 'text-white' },
};

let toastId = 0;
let addToastFn = null;

export const toast = {
  success: (msg) => addToastFn?.({ id: ++toastId, type: 'success', message: msg }),
  error: (msg) => addToastFn?.({ id: ++toastId, type: 'error', message: msg }),
  warning: (msg) => addToastFn?.({ id: ++toastId, type: 'warning', message: msg }),
  info: (msg) => addToastFn?.({ id: ++toastId, type: 'info', message: msg }),
};

const ToastItem = ({ t, onRemove }) => {
  const cfg = TOAST_TYPES[t.type];
  const Icon = cfg.icon;
  return (
    <motion.div
      initial={{ opacity: 0, x: 100, scale: 0.9 }}
      animate={{ opacity: 1, x: 0, scale: 1 }}
      exit={{ opacity: 0, x: 100, scale: 0.9 }}
      className={`flex items-center gap-3 px-5 py-4 rounded-2xl bg-gradient-to-r ${cfg.bg} shadow-xl border border-white/10 min-w-[320px]`}
    >
      <Icon className="w-5 h-5 text-white shrink-0" />
      <p className={`text-sm font-medium ${cfg.text} flex-1`}>{t.message}</p>
      <button onClick={() => onRemove(t.id)} className="text-white/70 hover:text-white transition-colors">
        <X className="w-4 h-4" />
      </button>
    </motion.div>
  );
};

export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);

  const addToast = useCallback((t) => {
    setToasts((prev) => [...prev, t]);
    setTimeout(() => setToasts((prev) => prev.filter((x) => x.id !== t.id)), 4000);
  }, []);

  useEffect(() => { addToastFn = addToast; }, [addToast]);

  const removeToast = (id) => setToasts((prev) => prev.filter((t) => t.id !== id));

  return (
    <>
      {children}
      <div className="fixed top-4 right-4 z-[9999] flex flex-col gap-3 pointer-events-none">
        <AnimatePresence>
          {toasts.map((t) => (
            <div key={t.id} className="pointer-events-auto">
              <ToastItem t={t} onRemove={removeToast} />
            </div>
          ))}
        </AnimatePresence>
      </div>
    </>
  );
};
